import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as R from 'ramda';
import haversine from 'haversine';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';

import { createSnack } from 'snackbar/snackbarSlice';

import { defaultMapCenter } from './Map/mapDefaults';
import {
  useGetAdditivesQuery,
  useGetBaseProductsQuery,
  useGetLocatorTerminalsQuery,
  useGetTerminalCitiesQuery,
} from './terminalLocatorEndpoints';
import { useGetTerminalAlertsCountQuery } from './timsEndpoints';

import {
  busyStatusWeight,
  CURRENT_LOCATION_ID,
  formatTerminalLocatorMileage,
  MAX_WAIT_TIMES_DISTANCE,
  SORT_BY,
} from './terminalLocatorConstants';

import SortBy from './TerminalLocatorSortBy';
import Filter from './TerminalLocatorFilter';
import TerminalList from './TerminalList';
import MobileHeader from './TerminalLocatorMobileHeader';
import MobileDrawer from './TerminalLocatorMobileDrawer';
import Map from './Map/Map';

const LEFT_COLUMN_WIDTH = '320px';

const styles = {
  clearFilters: {
    ml: 1,
    mr: 0,
    p: 1,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
    pt: { lg: 6, xs: 6.75 },
  },
  desktopFilters: {
    '> *': {
      '&.MuiAutocomplete-root': {
        flex: 1,
      },
      mx: 1,
      my: 0,
    },
    alignItems: 'center',
    backgroundColor: 'surface.white',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    overflowX: 'auto',
    p: 2,
  },
  desktopHeader: { display: 'flex' },
  listContainer: {
    backgroundColor: 'surface.light',
    minHeight: { md: 0, xs: '100%' },
    overflowY: 'auto',
    width: { md: LEFT_COLUMN_WIDTH, xs: '100%' },
  },
  locationFilter: {
    minWidth: '120px',
    ml: { lg: 0 },
  },
  mainWrapper: {
    display: 'flex',
    flex: 1,
    flexDirection: { md: 'row', xs: 'column ' },
    overflow: 'hidden',
  },
  mapContainer: {
    flex: 1,
    minHeight: { md: 0, xs: '100%' },
    position: 'relative',
    transform: { md: 'none', xs: 'translateY(-100%)' },
  },
  mapContainerHidden: {
    zIndex: -1,
  },
  mapContainerVisible: {
    zIndex: 1,
  },
  tabletFilters: {
    '> *': {
      flex: 1,
      mx: 1,
      my: 0,
    },
    alignItems: 'center',
    backgroundColor: 'surface.white',
    display: 'flex',
    overflowX: 'auto',
    px: 1.5,
    py: 2,
  },
};

const TerminalLocator = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'), { noSsr: true });
  const isTablet = useMediaQuery(theme => theme.breakpoints.between('md', 'lg'), { noSsr: true });
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const dispatch = useDispatch();

  const [sortBy, setSortBy] = useState('');
  const [location, setLocation] = useState(null);
  const [defaultLocationInitialized, setDefaultLocationInitialized] = useState(false);
  const [products, setProducts] = useState([]);
  const [additives, setAdditives] = useState([]);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState();
  const [mapViewActive, setMapViewActive] = useState(false);

  const [mapCenter, setMapCenter] = useState(defaultMapCenter);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const calculateDistances = terminal => {
    const distance = location && haversine(location, terminal, { unit: 'mile' });

    return {
      ...terminal,
      displayDistance: formatTerminalLocatorMileage(distance),
      distance,
    };
  };

  const filterProducts = terminal =>
    !products.length
      ? true
      : products.some(({ productId }) =>
          terminal.products.find(product => productId === product.productId)
        );

  const filterAdditives = terminal =>
    !additives.length
      ? true
      : additives.some(({ additiveIds }) =>
          additiveIds.some(additiveId =>
            terminal.additives.find(additive => additiveId === additive.additiveId)
          )
        );

  const filterMaxDistance =
    sortBy === SORT_BY.WAIT_TIMES.value
      ? ({ distance }) => distance < MAX_WAIT_TIMES_DISTANCE
      : () => true;

  const { data: citiesQuery = [] } = useGetTerminalCitiesQuery();
  const { data: productsQuery = [] } = useGetBaseProductsQuery();
  const { data: additivesQuery = [] } = useGetAdditivesQuery();
  const { data: alertsCountQuery = [] } = useGetTerminalAlertsCountQuery();

  const sortByDistance = R.ascend(R.prop('distance'));
  const sortByWaitTime = R.ascend(terminal =>
    busyStatusWeight(terminal.currentBusynessLevel ?? 'Unknown')
  );
  const sortComparator = R.cond([
    [R.equals(SORT_BY.DISTANCE.value), () => sortByDistance],
    [R.equals(SORT_BY.WAIT_TIMES.value), () => sortByWaitTime],
    [R.T, () => R.T],
  ]);

  const terminalsTransducer = R.compose(
    R.map(calculateDistances),
    R.map(terminal => ({
      ...terminal,
      alertCount: alertsCountQuery.find(
        ({ terminalId }) => terminalId === terminal.terminalId.toString()
      )?.alertCount,
    })),
    R.filter(filterProducts),
    R.filter(filterAdditives),
    R.filter(filterMaxDistance)
  );

  const { data: terminalsQuery, isLoading: isLoadingTerminals } = useGetLocatorTerminalsQuery(
    undefined,
    {
      selectFromResult: R.evolve({
        data: R.compose(
          R.sort(sortComparator(sortBy)),
          R.into([], terminalsTransducer),
          R.defaultTo([])
        ),
      }),
    }
  );

  useEffect(() => {
    if (citiesQuery.length && !location && !defaultLocationInitialized) {
      setLocation(citiesQuery.find(({ city }) => city === 'TULSA'));
      setDefaultLocationInitialized(true);
    }
  }, [location, citiesQuery, defaultLocationInitialized]);

  const handleClearFilters = () => {
    setLocation(citiesQuery.find(({ city }) => city === 'TULSA'));
    setProducts([]);
    setAdditives([]);
  };

  const productSelectAllRef = useRef([{ productIdName: 'Select All' }]);
  const additiveSelectAllRef = useRef([{ additiveIds: [], additiveName: 'Select All' }]);

  const allProducts = R.concat(productSelectAllRef.current, productsQuery);
  const allAdditives = R.concat(additiveSelectAllRef.current, additivesQuery);

  const FilterProducts = props => (
    <Filter
      disabled={!productsQuery.length}
      label="Products"
      multiple
      onApply={setProducts}
      optionLabel="productIdName"
      options={allProducts}
      selectAllLabel="All Products"
      value={products}
      {...props}
    />
  );
  const FilterAdditives = props => (
    <Filter
      disabled={!additivesQuery.length}
      label="Additives"
      multiple
      onApply={setAdditives}
      optionLabel="additiveName"
      options={allAdditives}
      selectAllLabel="All Additives"
      value={additives}
      {...props}
    />
  );

  const ClearFilters = props => (
    <Button onClick={handleClearFilters} sx={styles.clearFilters} variant="text" {...props}>
      Clear Filters
    </Button>
  );

  const DesktopFilters = () => (
    <Box sx={styles.desktopFilters}>
      <SetLocation />
      <Divider orientation="vertical" sx={{ height: 34, mx: 1 }} />
      <FilterProducts />
      <FilterAdditives />
      <ClearFilters />
    </Box>
  );

  const handleSetLocation = newLocation => {
    setSelectedTerminal(null);
    newLocation?.id === CURRENT_LOCATION_ID
      ? navigator.geolocation.getCurrentPosition(
          // On successful geolocation request
          ({ coords: { latitude, longitude } }) =>
            setLocation({
              city: 'Current Location',
              id: CURRENT_LOCATION_ID,
              label: 'Current Location',
              latitude,
              longitude,
            }),
          // On error
          () =>
            dispatch(
              createSnack({
                message: `Error getting user location`,
                options: { variant: 'error' },
              })
            )
        )
      : setLocation(newLocation);
  };

  const SetLocation = props => (
    <Filter
      blurOnSelect
      disabled={!citiesQuery.length}
      isOptionEqualToValue={({ id }, { id: value }) => id === value}
      label="Location"
      onApply={handleSetLocation}
      optionLabel="label"
      options={citiesQuery}
      sx={styles.locationFilter}
      value={location}
      {...props}
    />
  );

  // update map center on location filter change
  useEffect(() => {
    if (location) {
      const { latitude, longitude } = location;
      setMapCenter({ latitude, longitude });
    }
  }, [location]);

  // switch to map view on terminal selection in mobile only
  useEffect(() => {
    if (selectedTerminal && isMobile) {
      setMapViewActive(true);
    }
  }, [isMobile, selectedTerminal]);

  return (
    <Box sx={styles.container}>
      {isMobile && (
        <>
          <MobileHeader
            filterCount={products.length + additives.length}
            mapViewHook={[mapViewActive, setMapViewActive]}
            mobileDrawerHook={[mobileDrawerOpen, setMobileDrawerOpen]}
            SetLocation={SetLocation}
          />
          <MobileDrawer
            additivesHook={[additives, setAdditives]}
            allAdditives={allAdditives}
            allProducts={allProducts}
            ClearFilters={ClearFilters}
            FilterAdditives={FilterAdditives}
            FilterProducts={FilterProducts}
            location={location}
            mobileDrawerHook={[mobileDrawerOpen, setMobileDrawerOpen]}
            productsHook={[products, setProducts]}
            sortHook={[sortBy, setSortBy]}
          />
        </>
      )}
      {isTablet && (
        <Box sx={styles.tabletFilters}>
          <SortBy
            location={location}
            sortHook={[sortBy, setSortBy]}
            sx={{ display: 'flex', height: '100%', ml: 0 }}
          />
          <SetLocation />
          <FilterProducts />
          <FilterAdditives />
          <ClearFilters />
        </Box>
      )}
      {isDesktop && (
        <Box sx={styles.desktopHeader}>
          <SortBy
            location={location}
            sortHook={[sortBy, setSortBy]}
            sx={{
              backgroundColor: 'surface.white',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              width: LEFT_COLUMN_WIDTH,
            }}
          />
          <DesktopFilters />
        </Box>
      )}
      <Box sx={styles.mainWrapper}>
        <Box sx={styles.listContainer}>
          <TerminalList
            isLoadingTerminals={isLoadingTerminals}
            location={location}
            setSelectedTerminal={setSelectedTerminal}
            terminals={terminalsQuery}
          />
        </Box>
        <Box
          sx={[
            styles.mapContainer,
            isMobile && !mapViewActive ? styles.mapContainerHidden : styles.mapContainerVisible,
          ]}
        >
          <Map
            location={location}
            mapCenter={mapCenter}
            selectedTerminal={selectedTerminal}
            terminals={terminalsQuery}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TerminalLocator;
